import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../util/useIsMobile.ts';

// Generic searchable dropdown used across the builder's item / ability /
// move / nature / species pickers. Controlled by the parent — owns its own
// open/closed state and search input, but the selected value flows from
// props.
//
// Why one primitive: the five picker variants differ in option appearance
// (move slot shows type color + power; item shows just the name) but share
// the same selection mechanics — click to open, type to filter, click to
// pick, escape/click-outside to close. One implementation here, custom
// renderOption per caller.

export type PickerOption<T extends string = string> = {
  id: T;
  label: string;
  // Optional secondary search text (e.g. move type "Fire" so searching
  // "fire" matches Flamethrower without "fire" being in the move name).
  search?: string;
  // Custom render for the row in the dropdown. Defaults to the label.
  render?: ReactNode;
};

export function PickerField<T extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'select…',
  searchPlaceholder = 'search…',
  label,
  emptyText = 'no matches',
  disabled = false,
  triggerStyle,
}: {
  value: T | undefined;
  onChange: (next: T) => void;
  options: readonly PickerOption<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  emptyText?: string;
  disabled?: boolean;
  triggerStyle?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Close on click-outside. The parent can stuff this inside a card without
  // worrying about overlapping pickers — only one's popup is open at a time
  // because each opens via its own click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Autofocus the search input the moment the dropdown opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.search ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      {label && (
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
        >
          {label}
        </span>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="mono"
        style={{
          textAlign: 'left',
          fontSize: 12,
          padding: '7px 9px',
          background: 'var(--bg-2)',
          color: selected ? 'var(--fg-1)' : 'var(--fg-4)',
          border: `1px solid ${open ? 'var(--accent-line)' : 'var(--line-1)'}`,
          borderRadius: 3,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          ...triggerStyle,
        }}
      >
        {selected ? selected.label : placeholder}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 3,
            background: 'var(--bg-1)',
            border: '1px solid var(--line-2)',
            borderRadius: 3,
            zIndex: 20,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
          }}
        >
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="mono"
            style={{
              padding: '7px 9px',
              // iOS auto-zooms inputs with font-size < 16px. Bump on mobile
              // to keep the picker's viewport stable.
              fontSize: isMobile ? 16 : 12,
              color: 'var(--fg-1)',
              background: 'var(--bg-2)',
              border: 'none',
              borderBottom: '1px solid var(--line-1)',
              outline: 'none',
            }}
          />
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            {filtered.length === 0 ? (
              <div
                className="mono"
                style={{ padding: '12px 10px', fontSize: 11, color: 'var(--fg-4)' }}
              >
                {emptyText}
              </div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="mono"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    fontSize: 12,
                    color: opt.id === value ? 'var(--accent-2)' : 'var(--fg-1)',
                    background: opt.id === value ? 'var(--accent-bg)' : 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--line-1)',
                    cursor: 'pointer',
                  }}
                >
                  {opt.render ?? opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
